import type { FC } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import { ButtonCellSchevronLeftAddon } from "./components/LeftAddon/ButtonCellSchevronLeftAddon";
import ButtonCellSchevronRightAddon from "./components/RightAddon/ButtonCellSchevronRightAddon";
import { BUTTON_CELL_SCHEVRON_DISPLAY_NAME } from "./utils/core";
import { createTestIdForButtonCellSchevronElement } from "./utils/getTestId";
import { ButtonCell } from "..";
import { extractProps, getChildrenRecord } from "../../../utils/react";

import type { ButtonCellProps } from "..";

import classes from "./ButtonCellSchevron.styl";

export const BUTTON_CELL_SCHEVRON_TEST_ID =
    createTestIdForButtonCellSchevronElement("button-cell-schevron");

/**
 * Компонент кнопки, который отображает доступное действие в виде горизонтальной ячейки.
 *
 * Основан на компоненте ButtonCell и переиспользует динамические отступы, область нажатия и все состояния.
 *
 * По умолчанию отображает contentsView, leftIcon и rightIcon. LeftIcon можно заменять, rightIcon остается статичным - иконка шеврона "Перейти".
 * Может принимать в себя любые компоненты в bottomAddon, дополнительные слоты для удобного формирования наследников.
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?node-id=4220-52506&m=dev" target="_blank">Спека</a>
 * @constructor
 */
export const ButtonCellSchevron: FC<ButtonCellProps> = (props) => {
    const {
        customVarsStyles,
        currentProps: {
            state = "enabled",
            children,
            prefix = BUTTON_CELL_SCHEVRON_DISPLAY_NAME,
            "data-tsid": tsid = BUTTON_CELL_SCHEVRON_TEST_ID,
            ...restProps
        },
    } = useMemo(() => extractProps(props, ["customVarsStyles"]), [props]);

    const { Label, Subtitle, Value, LeftIcon, BottomAddon } = useMemo(
        () =>
            getChildrenRecord(children, prefix, {
                Label: {
                    required: true,
                },
                Unknown: {
                    skipElement: true,
                },
            }),
        [children, prefix],
    );

    const buttonCellSchevronStyles = cn(
        classes["button-cell-schevron"],
        customVarsStyles,
    );

    return (
        <ButtonCell
            {...restProps}
            state={state}
            customVarsStyles={buttonCellSchevronStyles}
            data-tsid={tsid}
            prefix={prefix}
            data-uikit={prefix}
        >
            {Label}

            {Subtitle}

            {Value}

            {LeftIcon && (
                <ButtonCellSchevronLeftAddon>
                    {LeftIcon}
                </ButtonCellSchevronLeftAddon>
            )}

            <ButtonCellSchevronRightAddon />

            {BottomAddon}
        </ButtonCell>
    );
};

ButtonCellSchevron.displayName = BUTTON_CELL_SCHEVRON_DISPLAY_NAME;

export default ButtonCellSchevron;
